import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Icon, type IconKey } from "../../icons";
import {
  apiFetch,
  getFleetKpi,
  listDriverStates,
  type AlertRecord,
  type DriverStateRecord,
  type FleetKpi,
} from "../../api";
import { LiveAnalysisPanel } from "./panels/LiveAnalysisPanel";
import { DriverFatiguePanel } from "./panels/DriverFatiguePanel";
import { DeliveryVerificationPanel } from "./panels/DeliveryVerificationPanel";
import { BorderSentinelPanel } from "./panels/BorderSentinelPanel";
import { ThreatDetectionPanel } from "./panels/ThreatDetectionPanel";
import { OverviewMap } from "./panels/OverviewMap";

interface VehicleRecord {
  vehicle_id: string;
  plate?: string | null;
  firm_id?: string;
}

interface Kpi {
  icon: IconKey;
  label: string;
  value: string;
  sub?: string;
  tone: "indigo" | "amber" | "emerald" | "rose" | "violet" | "neutral";
  mocked?: boolean;
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function formatRelative(iso: string, t: TFunction, locale?: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return t("overview.relative.justNow");
  if (m < 60) return t("overview.relative.minutesAgo", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("overview.relative.hoursAgo", { count: h });
  return new Date(iso).toLocaleString(locale);
}

function severityTone(s: string): string {
  const v = s.toLowerCase();
  if (v === "critical") return "rose";
  if (v === "high") return "amber";
  if (v === "medium") return "violet";
  return "neutral";
}

export function OverviewPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRecord[]>([]);
  const [kpi, setKpi] = useState<FleetKpi | null>(null);
  const [drivers, setDrivers] = useState<DriverStateRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [alertList, vehicleList, kpiData, driverList] = await Promise.all([
          apiFetch<AlertRecord[]>("/api/v1/alerts").catch(() => [] as AlertRecord[]),
          apiFetch<VehicleRecord[]>("/api/v1/registry/vehicles").catch(
            () => [] as VehicleRecord[],
          ),
          getFleetKpi().catch(() => null),
          listDriverStates({ risky_only: true }).catch(() => [] as DriverStateRecord[]),
        ]);
        if (cancelled) return;
        setAlerts(Array.isArray(alertList) ? alertList : []);
        setVehicles(Array.isArray(vehicleList) ? vehicleList : []);
        setKpi(kpiData);
        setDrivers(Array.isArray(driverList) ? driverList : []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "load error");
      }
    };
    void load();
    const timer = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const openAlerts = alerts.filter((a) => a.status.toLowerCase() === "open").length;
  const criticalOpen = alerts.filter(
    (a) =>
      a.status.toLowerCase() === "open" &&
      a.severity.toLowerCase() === "critical",
  ).length;

  // KPIs: 3 real (Aktif Araç, Açık Alarm, Günlük Olay), 4 mock (sürücü riski,
  // AI müdahale, yakıt sarfiyatı anormali, sürüş skoru). Mocked KPIs are
  // tagged with "Demo veri" to keep the demo honest.
  const totalVehicles = vehicles.length || 0;
  const activeVehicles = totalVehicles; // until live "last-seen" exists
  const todayAlerts = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const cutoff = midnight.getTime();
    return alerts.filter((a) => new Date(a.occurred_at).getTime() >= cutoff).length;
  }, [alerts]);

  const kpis: Kpi[] = [
    {
      icon: "truck",
      label: t("overview.kpi.activeVehicles"),
      value: totalVehicles > 0 ? `${activeVehicles}/${totalVehicles}` : "0/0",
      sub: totalVehicles > 0 ? t("overview.kpi.activePct", { pct: pct(activeVehicles / totalVehicles) }) : t("overview.kpi.noRecord"),
      tone: "indigo",
    },
    {
      icon: "alert",
      label: t("overview.kpi.openAlerts"),
      value: openAlerts.toString(),
      sub: criticalOpen > 0 ? t("overview.kpi.critical", { count: criticalOpen }) : t("overview.kpi.noCritical"),
      tone: openAlerts > 0 ? "rose" : "emerald",
    },
    {
      icon: "bolt",
      label: t("overview.kpi.todayEvents"),
      value: todayAlerts.toString(),
      sub: t("overview.kpi.last24h"),
      tone: "violet",
    },
    {
      icon: "eye",
      label: t("overview.kpi.driverRisk"),
      value: drivers.length.toString(),
      sub: drivers.length > 0 ? t("overview.kpi.highCritical") : t("overview.kpi.noRisk"),
      tone: drivers.length > 0 ? "rose" : "emerald",
    },
    {
      icon: "receipt",
      label: t("overview.kpi.pendingExpense"),
      value: kpi ? String(kpi.expenses_pending) : "—",
      sub: kpi
        ? `${kpi.expenses_pending_total.toLocaleString(i18n.resolvedLanguage)} TRY`
        : t("overview.kpi.loading"),
      tone: "violet",
    },
    {
      icon: "fuel",
      label: t("overview.kpi.fuelAnomaly"),
      value: kpi ? String(kpi.fuel_anomalies_open) : "—",
      sub: t("overview.kpi.openRecord"),
      tone: kpi && kpi.fuel_anomalies_open > 0 ? "amber" : "emerald",
    },
    {
      icon: "shield",
      label: t("overview.kpi.safeDriving"),
      value: drivers.length === 0 ? "100%" : `${Math.max(0, 100 - drivers.length * 10)}%`,
      sub: t("overview.kpi.fleetScore"),
      tone: "indigo",
    },
  ];

  return (
    <div className="overview">
      {error && (
        <div className="panel error-banner">
          <Icon name="alert" /> {t("overview.dataError", { error })}
        </div>
      )}

      <div className="kpi-grid">
        {kpis.map((k) => (
          <div key={k.label} className={`kpi-card tone-${k.tone}${k.mocked ? " kpi-mocked" : ""}`}>
            <div className="kpi-icon">
              <Icon name={k.icon} size={22} />
            </div>
            <div className="kpi-body">
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value">{k.value}</div>
              {k.sub && <div className="kpi-sub">{k.sub}</div>}
            </div>
            {k.mocked && <span className="kpi-mock-tag">Demo</span>}
          </div>
        ))}
      </div>

      <div className="overview-row">
        <section className="panel overview-feed">
          <header className="panel-head">
            <h3>
              <Icon name="bolt" size={18} /> {t("overview.feedTitle")}
            </h3>
            <Link to="/events" className="panel-link">
              {t("overview.all")} <span aria-hidden>&rarr;</span>
            </Link>
          </header>
          <ul className="feed-list">
            {alerts.slice(0, 8).map((a) => (
              <li key={a.event_id} className={`feed-item tone-${severityTone(a.severity)}`}>
                <div className="feed-dot" />
                <div className="feed-main">
                  <div className="feed-title">{a.summary || a.code}</div>
                  <div className="feed-meta">
                    <span>{a.vehicle_id}</span>
                    <span>·</span>
                    <span>{formatRelative(a.occurred_at, t, i18n.resolvedLanguage)}</span>
                  </div>
                </div>
                <span className={`severity-pill sev-${a.severity.toLowerCase()}`}>
                  {a.severity}
                </span>
              </li>
            ))}
            {alerts.length === 0 && (
              <li className="feed-empty">
                <Icon name="check" /> {t("overview.noActiveEvent")}
              </li>
            )}
          </ul>
        </section>

        <section className="panel overview-map">
          <header className="panel-head">
            <h3>
              <Icon name="map" size={18} /> {t("overview.liveMapTitle")}
            </h3>
            <Link to="/map" className="panel-link">
              {t("overview.fullscreen")} <span aria-hidden>&rarr;</span>
            </Link>
          </header>
          <OverviewMap vehicles={vehicles} />
        </section>

        <section className="panel overview-threat">
          <ThreatDetectionPanel />
        </section>
      </div>

      <div className="overview-row">
        <section className="panel overview-driver">
          <DriverFatiguePanel />
        </section>
        <section className="panel overview-delivery">
          <DeliveryVerificationPanel />
        </section>
        <section className="panel overview-border">
          <BorderSentinelPanel />
        </section>
      </div>

      <section className="panel overview-live">
        <header className="panel-head">
          <h3>
            <Icon name="video" size={18} /> {t("overview.liveTitle")}
          </h3>
          <span className="panel-tag">{t("overview.liveTag")}</span>
        </header>
        <LiveAnalysisPanel />
      </section>

      <section className="panel overview-timeline">
        <header className="panel-head">
          <h3>
            <Icon name="clock" size={18} /> {t("overview.timelineTitle")}
          </h3>
          <span className="panel-tag">{t("overview.timelineTag")}</span>
        </header>
        <Timeline alerts={alerts.slice(0, 8)} t={t} locale={i18n.resolvedLanguage} />
      </section>
    </div>
  );
}

function Timeline({ alerts, t, locale }: { alerts: AlertRecord[]; t: TFunction; locale?: string }) {
  if (alerts.length === 0) {
    return (
      <div className="timeline-empty">
        <Icon name="check" /> {t("overview.noEvents")}
      </div>
    );
  }
  return (
    <ol className="timeline">
      {alerts.map((a, i) => (
        <li key={a.event_id} className={`timeline-item tone-${severityTone(a.severity)}`}>
          <div className="timeline-time">
            {new Date(a.occurred_at).toLocaleTimeString(locale, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div className="timeline-dot">
            <span>{i + 1}</span>
          </div>
          <div className="timeline-body">
            <div className="timeline-title">{a.summary || a.code}</div>
            <div className="timeline-meta">
              {a.vehicle_id} · {a.severity}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
